import React, { useState, useRef, useEffect } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import styled from 'styled-components';
import {
  cssColorValue,
  semanticColors,
  spacer12,
  spacer16,
  spacer24,
  Tooltip,
  TooltipTrigger,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { useClipboard } from '@hooks/useClipboard';
import { buildAddSnackbarAction } from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import copyToGreenCheckAnimation from '@components/shared/CopyToGreenCheckAnimation/copyToGreenCheckAnimation.json';

const ReleaseIDType = styled(Type)`
  padding-left: ${spacer16};
  margin-right: ${spacer12};
  text-overflow: ellipsis;
  overflow: hidden;
  width: 100%;
  text-wrap: nowrap;
`;

const ReleaseIdContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 180px;
  border-radius: 40px;
  cursor: pointer;
  background-color: transparent;
  border-color: transparent;

  :hover,
  :focus {
    background-color: ${cssColorValue(semanticColors.backgroundTintedBase)};
  }

  :active {
    background-color: ${cssColorValue(semanticColors.backgroundPress)};
  }
`;

const IconContainer = styled.div`
  width: 36px;
  height: 36px;
  padding-top: 6px;
  border-radius: 50%;

  &:hover {
    cursor: pointer;
  }
`;

interface CopyReleaseIdProps {
  primaryReleaseId: string;
  gaHandleClickCopyReleaseId?: () => void;
}

export const CopyReleaseId = ({
  primaryReleaseId,
  gaHandleClickCopyReleaseId,
}: CopyReleaseIdProps) => {
  const t = useT();
  const [showTooltip, toggleShowTooltip] = useState<boolean>(false);
  const [showCopyAnimation, toggleShowCopyAnimation] = useState<boolean>(false);

  const { copyText: copyToClipboard } = useClipboard(primaryReleaseId);
  const [, dispatch] = useSnackbarNotifications();

  const lottieRef = useRef<Player>(null);
  const playAnimation = () => {
    if (lottieRef.current) {
      lottieRef.current.play();
    }
  };

  useEffect(() => {
    if (showCopyAnimation) {
      playAnimation();
    }
  }, [showCopyAnimation]);

  const handleClickCopyReleaseId = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (gaHandleClickCopyReleaseId) {
      gaHandleClickCopyReleaseId();
    }

    toggleShowCopyAnimation(true);
    copyToClipboard();
    dispatch(
      buildAddSnackbarAction(
        t(
          'atlas.snackbars.copied_to_clipboard',
          'Copied to clipboard',
          'Snackbar message to indicate copied to clipboard',
        ),
      ),
    );
  };

  return (
    <TooltipTrigger
      overlay={
        showTooltip && (
          <Tooltip>
            {t(
              'atlas.actions.copy_id',
              'Copy ID',
              'Tooltip message that indicates user copy ID',
            )}
          </Tooltip>
        )
      }
      onShow={() => toggleShowTooltip(true)}
      onHide={() => toggleShowTooltip(false)}
      placement={TooltipTrigger.top}
    >
      <ReleaseIdContainer
        as="button"
        onClick={handleClickCopyReleaseId}
        aria-label="Release ID"
      >
        <ReleaseIDType variant="viola" semanticColor="textBase">
          {primaryReleaseId}
        </ReleaseIDType>
        <IconContainer aria-label="green-check-animation">
          <Player
            ref={lottieRef}
            src={copyToGreenCheckAnimation}
            style={{
              height: `${spacer24}`,
              width: `${spacer24}`,
            }}
            onEvent={(event: string) => {
              if (event === 'complete') {
                toggleShowCopyAnimation(false);
              }
            }}
          />
        </IconContainer>
      </ReleaseIdContainer>
    </TooltipTrigger>
  );
};
